import SendIcon from '@mui/icons-material/Send';
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {styled} from '@mui/material/styles';
import LoadingButton from 'common/components/loading-button';
import EmailAddress from 'common/values/email-address/email-address';
import EmailAddressInput from 'common/values/email-address/view/email-address-input';
import Name from 'common/values/name/name';
import NameInput from 'common/values/name/view/name-input';
import EntityOfficerInvitationAPIService
  from 'legal-entities/entities/entity-officer-invitation/api/entity-officer-invitation-api-service';
import EntityOfficerInvitation, {
  InvalidEntityOfficerInvitationError
} from 'legal-entities/entities/entity-officer-invitation/entity-officer-invitation';
import EntityName from 'legal-entities/values/entity-name/entity-name';
import EntityNameInput from 'legal-entities/values/entity-name/view/entity-name-input';
import InvitationMessage from 'legal-entities/values/invitation-message/invitation-message';
import InvitationMessageInput from 'legal-entities/values/invitation-message/view/invitation-message-input';
import RegistrationInfo, {InvalidRegistrationInfoError} from 'legal-entities/values/registration-info';
import {enqueueSnackbar} from 'notistack';
import {useState} from 'react';
import Session from 'users/session/session';

const InvitationForm = styled(Paper)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2)
  },
  padding: theme.spacing(6),
  position: 'relative',
  width: '100%'
}));
const InvitationInputContainer = styled(Box)(({ theme }) => ({
  maxWidth: theme.spacing(30)
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5)
}));

type InvitationFormErrors = {
  general?: string;
  email?: string;
  name?: string;
  entityName?: string;
  message?: string;
}

type EntityOfficerInvitationFormProps = {
  className?: string;
  session: Readonly<Session>;
  onInvitationCreated: (invitation: EntityOfficerInvitation) => void;
}

export default function EntityOfficerInvitationForm(props: Readonly<EntityOfficerInvitationFormProps>) {
  const { 
    className, 
    session, 
    onInvitationCreated 
  } = props;

  const [invitationEmail, setInvitationEmail] = useState<EmailAddress>();
  const [invitationEntityName, setInvitationEntityName] = useState<EntityName>();
  const [invitationName, setInvitationName] = useState<Name>();
  const [invitationMessage, setInvitationMessage] = useState<InvitationMessage>();
  const [formErrors, setFormErrors] = useState<InvitationFormErrors>();
  const [sending, setSending] = useState<boolean>(false);

  function buildNewInvitation(): EntityOfficerInvitation | undefined {
    if (!formValid()) return;

    try {
      const invitationRegistrationInfo = new RegistrationInfo(invitationName!, invitationEmail!);
      return new EntityOfficerInvitation(
        invitationEntityName!,
        invitationRegistrationInfo,
        invitationMessage
      );
    } catch (error) {
      if (error instanceof InvalidEntityOfficerInvitationError ||
        error instanceof InvalidRegistrationInfoError) {
        let errors: InvitationFormErrors = {};
        errors.general = error.message;
        setFormErrors(errors);
        return;
      }
      throw error;
    }
  }

  async function handleSendInvite() {
    setSending(true);

    try {
      const builtInvitation = buildNewInvitation();
      if (!builtInvitation) 
        throw new InvalidEntityOfficerInvitationError("Invitation is invalid");
      const service = new EntityOfficerInvitationAPIService(session);
      const updatedInvitation = await service.inviteEntityOfficer(builtInvitation);
      enqueueSnackbar("Sent entity officer invitation", { variant: 'success' });
      onInvitationCreated(updatedInvitation);
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Failed to send entity officer invitation", { variant: 'error' });
    } finally {
      clearForm();
      setSending(false);
    }
  }

  function formValid(): boolean {
    return (
      invitationEmail !== undefined &&
      invitationEntityName !== undefined &&
      invitationName !== undefined
    );
  }

  function updateFormErrors(type: keyof InvitationFormErrors, error?: string) {
    if (!error) return;

    let errors: InvitationFormErrors = { ...formErrors };
    errors[type] = error;
    setFormErrors(errors);
  }

  function clearForm() {
    setInvitationEmail(undefined);
    setInvitationEntityName(undefined);
    setInvitationName(undefined);
    setInvitationMessage(undefined);
  }

  return (
    <InvitationForm className={className}>
      <InvitationFormTitle variant='h6'>
        Invite Entity Officer
      </InvitationFormTitle>
      <InvitationInputContainer>
        <EmailAddressInput
          idPrefix="officer-invitation"
          emailAddress={invitationEmail}
          onChange={(updatedEmail?: EmailAddress, error?: string) => {
            if (updatedEmail) setInvitationEmail(updatedEmail);
            updateFormErrors('email', error);
            buildNewInvitation();
          }}
        />
        <NameInput
          idPrefix="officer-invitation"
          name={invitationName}
          onChange={(updatedName?: Name | undefined, error?: string) => {
            if (updatedName) setInvitationName(updatedName);
            updateFormErrors('name', error);
            buildNewInvitation();
          }}
        />
        <EntityNameInput
          idPrefix="officer-invitation"
          entityName={invitationEntityName}
          onChange={(updatedEntityName?: EntityName | undefined, error?: string) => {
            if (updatedEntityName) setInvitationEntityName(updatedEntityName);
            updateFormErrors('entityName', error);
            buildNewInvitation();
          }}
        />
        <InvitationMessageInput
          idPrefix="officer-invitation"
          message={invitationMessage}
          onChange={(updatedMessage?: InvitationMessage, error?: string) => {
            if (updatedMessage) setInvitationMessage(updatedMessage);
            updateFormErrors('message', error);
            buildNewInvitation();
          }}
        />
        <LoadingButton
          id="officer-invitation-submit-button"
          color="primary"
          disabled={sending || !formValid()}
          loading={sending}
          onClick={handleSendInvite}
          startIcon={<SendIcon />}
          variant='contained'>
          Send Invite
        </LoadingButton>
      </InvitationInputContainer>
    </InvitationForm>
  )
}
